import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { GridColDef } from '@mui/x-data-grid';

import { FDataGrid } from '../grid';
import {
  ISimulationCreateScenarioRequest,
  ISimulationManagerWorkbench,
  ISimulationScenarioRow,
} from './simulation.models';

type Props = {
  workbench: ISimulationManagerWorkbench;
  loading?: boolean;
  onRefresh: () => void | Promise<void>;
  onCreate: (request: ISimulationCreateScenarioRequest) => Promise<void>;
  onOpen: (scenario: ISimulationScenarioRow) => void;
};

type State = {
  createOpen: boolean;
  name: string;
  typecode: string;
  parentid: number | '';
  description: string;
  error: string;
  selectedScenarioId: number | null;
};

class SimulationManager extends React.Component<Props, State> {
  state: State = {
    createOpen: false,
    name: '',
    typecode: 'BASE',
    parentid: '',
    description: '',
    error: '',
    selectedScenarioId: null,
  };

  private openCreate = () => {
    const w = this.props.workbench;
    const baseline = w.scenarios.find((x) => x.baseline) ?? w.scenarios[0];
    const first = w.summary.scenariocount === 0;

    this.setState({
      createOpen: true,
      name: first
        ? `${w.context.fiscalyear || ''} Base Simulation`.trim()
        : `${w.context.fiscalyear || ''} Alternative Simulation`.trim(),
      typecode: baseline?.typecode || 'BASE',
      parentid: first ? '' : (baseline?.rid ?? ''),
      description: first
        ? 'Initial Simulation scenario from the published Forecast Baseline.'
        : 'Alternative Simulation scenario derived from the governed baseline.',
      error: '',
    });
  };

  private create = async () => {
    if (!this.state.name.trim()) {
      this.setState({ error: 'Scenario name is required.' });
      return;
    }
    if (!this.state.typecode.trim()) {
      this.setState({ error: 'Scenario type is required.' });
      return;
    }

    try {
      await this.props.onCreate({
        planningcycleid: this.props.workbench.context.planningcycleid,
        scenarioname: this.state.name.trim(),
        scenariotypecode: this.state.typecode.trim().toUpperCase(),
        parentscenarioid:
          this.state.parentid === '' ? null : Number(this.state.parentid),
        description: this.state.description.trim(),
      });
      this.setState({ createOpen: false, error: '' });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    }
  };

  private columns: GridColDef[] = [
    {
      field: 'nam',
      headerName: 'Scenario',
      minWidth: 280,
      flex: 1,
      renderCell: (p) => (
        <Stack spacing={0.2} sx={{ py: 0.5, overflow: 'hidden' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
              {p.row.nam}
            </Typography>
            {p.row.baseline && (
              <Chip
                size="small"
                label="Baseline"
                color="info"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap>
            {p.row.shc}
          </Typography>
        </Stack>
      ),
    },
    { field: 'typecode', headerName: 'Type', width: 120 },
    {
      field: 'statecode',
      headerName: 'State',
      width: 140,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.value || 'DRAFT'}
          color={
            String(p.value).toUpperCase() === 'APPROVED'
              ? 'success'
              : String(p.value).toUpperCase() === 'SUBMITTED'
                ? 'info'
                : 'default'
          }
        />
      ),
    },
    {
      field: 'origin',
      headerName: 'Origin',
      width: 170,
      valueGetter: (_value, row: ISimulationScenarioRow) =>
        row.parentid ? 'Parent Scenario' : 'Published Forecast',
    },
    {
      field: 'created',
      headerName: 'Created',
      width: 185,
      valueFormatter: (value) =>
        value ? new Date(String(value)).toLocaleString() : '-',
    },
    {
      field: 'open',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Button
          size="small"
          onClick={() => this.props.onOpen(p.row as ISimulationScenarioRow)}
        >
          <OpenInNewIcon fontSize="small" />
        </Button>
      ),
    },
  ];

  render() {
    const w = this.props.workbench;
    const ctx = w.context;

    return (
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Published Forecast Baseline
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {ctx.forecastbatchname ||
                  ctx.forecastbatchcode ||
                  'Not available'}
              </Typography>
              {ctx.forecastpublishedat && (
                <Typography variant="caption" color="text.secondary">
                  Published {new Date(ctx.forecastpublishedat).toLocaleString()}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Scenarios
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {w.summary.scenariocount}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Draft
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {w.summary.draftcount}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Submitted
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {w.summary.submittedcount}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Approved
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {w.summary.approvedcount}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Alert
          severity={w.summary.publishedforecastavailable ? 'info' : 'warning'}
        >
          {w.guidance?.message || 'Create or open a Simulation scenario.'}
        </Alert>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!w.actions.canCreateScenario || this.props.loading}
            onClick={this.openCreate}
          >
            Create Scenario
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={this.props.loading}
            onClick={this.props.onRefresh}
          >
            Refresh
          </Button>
        </Stack>

        <FDataGrid
          title="Simulation Scenarios"
          subtitle="Each scenario is governed by the current Planning Cycle and published Forecast Baseline."
          rows={w.scenarios}
          columns={this.columns}
          loading={this.props.loading}
          height={520}
          selectionMode={'single'}
          showSelectionControl
          selectedRowId={this.state.selectedScenarioId}
          onSelectedRowChange={(row) =>
            this.setState({
              selectedScenarioId: row
                ? Number((row as ISimulationScenarioRow).rid)
                : null,
            })
          }
          showQuickFilter
          getRowId={(r: any) => r.rid}
          onRowDoubleClick={(params) =>
            this.props.onOpen(params.row as ISimulationScenarioRow)
          }
          pageSizeOptions={[10, 25, 50]}
        />

        <Dialog
          open={this.state.createOpen}
          onClose={() => this.setState({ createOpen: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create Simulation Scenario</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {this.state.error && (
                <Alert severity="error">{this.state.error}</Alert>
              )}
              <Alert severity="info">
                {w.summary.scenariocount === 0
                  ? 'The first root scenario is initialized from the published Forecast Baseline.'
                  : 'An alternative scenario can inherit the governed values of an existing parent scenario.'}
              </Alert>
              <TextField
                label="Scenario Name"
                value={this.state.name}
                fullWidth
                onChange={(e) => this.setState({ name: e.target.value })}
              />
              <TextField
                label="Scenario Type Code"
                value={this.state.typecode}
                fullWidth
                helperText="Uses the active Simulation Scenario Type registry."
                onChange={(e) =>
                  this.setState({ typecode: e.target.value.toUpperCase() })
                }
              />
              <TextField
                select
                label="Parent Scenario"
                value={this.state.parentid}
                fullWidth
                helperText="Leave blank for a root scenario. Alternatives should normally derive from the baseline."
                onChange={(e) => {
                  const parentid =
                    e.target.value === '' ? '' : Number(e.target.value);
                  const parent = w.scenarios.find((x) => x.rid === parentid);
                  this.setState({
                    parentid,
                    typecode: parent?.typecode || this.state.typecode,
                  });
                }}
              >
                <MenuItem value="">
                  No parent — initialize from published Forecast
                </MenuItem>
                {w.scenarios.map((s) => (
                  <MenuItem key={s.rid} value={s.rid}>
                    {s.nam} {s.baseline ? '(Baseline)' : ''}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Description"
                value={this.state.description}
                fullWidth
                multiline
                minRows={3}
                onChange={(e) => this.setState({ description: e.target.value })}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ createOpen: false })}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={this.create}
              disabled={this.props.loading}
            >
              Create Scenario
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }
}

export default SimulationManager;
